/**
 * Pure path helpers for the `/open/*` deep-link resolver.
 *
 * The public help center (help.scrambl.com) cannot know which organization a
 * reader belongs to, so in-app links are published as
 * `https://app.scrambl.com/open/<path>` (e.g. `/open/admin/settings`). This
 * module turns the router splat back into an in-app path; the React component
 * then resolves the organization prefix via `resolveHelpAppPath`.
 *
 * Kept free of React so the open-redirect guard can be unit tested directly.
 */

/** Where `/open` with no path lands. */
export const OPEN_ROUTE_FALLBACK_PATH = '/today'

/**
 * Rebuild the in-app path from the `/open/*` splat, preserving query + hash.
 *
 * SECURITY: the result is ALWAYS a single-slash-prefixed relative path. Every
 * leading slash/backslash is stripped from the splat before we prefix exactly
 * one `/`, so a crafted link such as `/open///evil.com` can never produce a
 * protocol-relative (`//evil.com`) or absolute destination — it degrades to the
 * harmless in-app path `/evil.com`.
 */
export function buildOpenTargetPath(
  splat: string | undefined | null,
  search = '',
  hash = '',
): string {
  const cleaned = (splat ?? '')
    .trim()
    // Open-redirect guard: no leading slashes, backslashes or whitespace.
    .replace(/^[/\\\s]+/, '')
    // Windows-style separators are never meaningful in a route.
    .replace(/\\/g, '/')

  if (!cleaned) {
    return `${OPEN_ROUTE_FALLBACK_PATH}${search}${hash}`
  }

  return `/${cleaned}${search}${hash}`
}
